import React from 'react';
import { getIn } from 'formik';
import { COUNTRIES, getCountryFlag } from '../../../utils/countries';
import { Chips } from '../components/Chips';
import { EyeIcon, Field, LockIcon, MailIcon, PinIcon } from '../components/Field';
import { PasswordStrength } from '../components/PasswordStrength';
import { ApplicationStepProps } from '../interfaces';

/**
 * Número de ejemplo para los campos de teléfono.
 *
 * Los cincos repetidos son la convención de número ficticio: se lee como un ejemplo y no
 * puede corresponder a una línea real de nadie.
 */
const PHONE_PLACEHOLDER = '5555 5555';

/**
 * Cómo se llama la división administrativa en cada país.
 *
 * El formulario es regional y "Provincia" no es lo que lee un comercio chileno ni uno
 * brasileño. Los que no están en el mapa caen en un rótulo neutro.
 */
const STATE_LABELS: Record<string, string> = {
  AR: 'Provincia',
  BO: 'Departamento',
  BR: 'Estado',
  CL: 'Región',
  CO: 'Departamento',
  EC: 'Provincia',
  ES: 'Provincia',
  MX: 'Estado',
  PE: 'Región',
  PY: 'Departamento',
  US: 'Estado',
  UY: 'Departamento',
  VE: 'Estado',
};

const CommerceStep: React.FC<ApplicationStepProps> = ({ controller }) => {
  const { formik } = controller;
  const [showPassword, setShowPassword] = React.useState(false);
  const [showOptional, setShowOptional] = React.useState(false);

  const error = (field: string) =>
    getIn(formik.touched, field) && getIn(formik.errors, field)
      ? (getIn(formik.errors, field) as string)
      : undefined;

  const inputClass = (field: string, extra = '') =>
    `bk-input${error(field) ? ' bk-input--error' : ''}${extra}`;

  const typeOptions = controller.types.map(type => ({
    value: type._id,
    label: type.label,
  }));

  const countryIso = formik.values.country?.value;
  const countryPrefix = formik.values.country?.prefix ?? '';
  const countryFlag = getCountryFlag(countryIso);
  const stateLabel = STATE_LABELS[countryIso ?? ''] ?? 'Provincia o estado';
  const postalLabel = countryIso === 'US' ? 'ZIP code' : 'Código postal';

  /**
   * El select de país: se comparte entre la ubicación y el prefijo del teléfono.
   *
   * La clave es `iso2` y no `iso`: en `states-cities-db` el campo `iso` es el de tres
   * letras (`ARG`) y el que devuelve el geocoder —y el que guarda `locationMetadata`— es
   * el de dos (`AR`). Con el de tres la detección no encontraba nunca el país y la
   * bandera quedaba vacía.
   */
  const changeCountry = (iso2: string) => {
    const country = COUNTRIES.find(item => item.iso2 === iso2);

    formik.setFieldValue(
      'country',
      country
        ? { label: country.name, value: country.iso2, prefix: country.prefix }
        : null,
      true
    );
  };

  return (
    <div className="bk-fields">
      {/* Sin rótulo: el stepper ya titula el paso "Tu comercio" y repetirlo acá dejaba
          dos labels idénticos, uno debajo del otro. */}
      <section className="bk-group">
        <Field id="name" label="Nombre del comercio" error={error('name')}>
          <input
            id="name"
            name="name"
            className={inputClass('name')}
            maxLength={60}
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Bici Store"
          />
        </Field>

        <div className="bk-field">
          <span className="bk-label" id="types-label">
            Tipo de comercio
          </span>
          {controller.isLoadingTypes ? (
            <p className="bk-help">Cargando categorías...</p>
          ) : controller.typesError ? (
            <>
              <span className="bk-error">{controller.typesError}</span>
              <button
                type="button"
                className="bk-collapse"
                onClick={controller.retryTypes}
              >
                Volver a intentar
              </button>
            </>
          ) : (
            <>
              <Chips
                label="Tipo de comercio"
                mode="multiple"
                options={typeOptions}
                selected={formik.values.types}
                onToggle={controller.toggleType}
              />
              {error('types') ? (
                <span className="bk-error">{error('types')}</span>
              ) : (
                <span className="bk-help">
                  Puedes elegir más de una si tu comercio abarca varias.
                </span>
              )}
            </>
          )}
        </div>

        <div className="bk-field">
          <label className="bk-label" htmlFor="address">
            Dirección
          </label>
          {/* La búsqueda es explícita: el botón o Enter. El campo es controlado para que al
            elegir una opción muestre la dirección completa. */}
          <div className="bk-search">
            <div className="bk-input-wrap">
              <input
                id="address"
                className={inputClass(
                  'address',
                  formik.values.point.lat ? ' bk-input--checked' : ''
                )}
                value={controller.addressQuery}
                onChange={event =>
                  controller.changeAddressQuery(event.target.value)
                }
                onKeyDown={event => {
                  if (event.key !== 'Enter') return;
                  // Sin esto, Enter dispara el submit del form.
                  event.preventDefault();
                  controller.searchAddress(controller.addressQuery);
                }}
                placeholder="Calle, número y ciudad"
                autoComplete="off"
              />
              {formik.values.point.lat && (
                <span className="bk-check" aria-hidden="true">
                  &#10003;
                </span>
              )}
            </div>
            <button
              type="button"
              className="bk-btn bk-btn--compact"
              disabled={controller.isSearchingAddress}
              onClick={() => controller.searchAddress(controller.addressQuery)}
            >
              {controller.isSearchingAddress ? 'Buscando…' : 'Buscar'}
            </button>
          </div>

          {error('address') || error('point.lat') ? (
            <span className="bk-error">
              {error('address') || error('point.lat')}
            </span>
          ) : controller.addressNotice ? (
            <span className="bk-help">{controller.addressNotice}</span>
          ) : formik.values.point.lat ? (
            <span className="bk-help">
              Con esta dirección completamos la provincia, la ciudad y el país.
            </span>
          ) : (
            <span className="bk-help">
              Escribe la dirección, toca Buscar y elige una de las opciones.
            </span>
          )}

          {/*
            Los resultados se leen como una lista de opciones y no como un bloque de
            texto: antes eran botones sin más marca que el hover y no se entendía que
            había que elegir uno. Ahora el panel se anuncia con un título, cada opción
            lleva su pin y la calle va separada del resto de la dirección.
          */}
          {controller.addressResults.length > 0 && (
            <div className="bk-results">
              <p className="bk-results__title">
                Elige tu dirección
                <span className="bk-results__count">
                  {controller.addressResults.length === 1
                    ? '1 resultado'
                    : `${controller.addressResults.length} resultados`}
                </span>
              </p>
              {controller.addressResults.map((result, index) => {
                const [street, ...rest] = result.displayName.split(',');

                return (
                  <button
                    key={`address-${index}`}
                    type="button"
                    className="bk-result"
                    onClick={() => controller.selectAddress(result)}
                  >
                    <PinIcon />
                    <span className="bk-result__text">
                      <span className="bk-result__street">{street}</span>
                      {rest.length > 0 && (
                        <span className="bk-result__rest">
                          {rest.join(',').trim()}
                        </span>
                      )}
                    </span>
                    <span className="bk-result__pick" aria-hidden="true">
                      Elegir
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/*
          Provincia, ciudad, código postal y país salen de la dirección elegida y quedan
          editables. El país se resuelve una sola vez acá: el prefijo del teléfono y el
          del WhatsApp se derivan de este mismo valor, así no hay que elegirlo de nuevo
          más abajo.
        */}
        <div className="bk-pair">
          <Field id="state" label={stateLabel} error={error('state')}>
            <input
              id="state"
              name="state"
              className={inputClass('state')}
              maxLength={80}
              value={formik.values.state}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder={stateLabel}
            />
          </Field>
          <Field id="city" label="Ciudad" error={error('city')}>
            <input
              id="city"
              name="city"
              className={inputClass('city')}
              maxLength={80}
              value={formik.values.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Ciudad o comuna"
            />
          </Field>
        </div>

        <div className="bk-pair">
          <div className="bk-field">
            <label className="bk-label" htmlFor="country">
              País
            </label>
            <div className="bk-select-wrap bk-select-wrap--flag">
              {countryFlag && (
                <span className="bk-flag" aria-hidden="true">
                  {countryFlag}
                </span>
              )}
              <select
                id="country"
                className={`bk-select${countryFlag ? ' bk-select--flag' : ''}${
                  error('country') ? ' bk-select--error' : ''
                }`}
                value={formik.values.country?.value ?? ''}
                onChange={event => changeCountry(event.target.value)}
              >
                <option value="">Elige un país</option>
                {COUNTRIES.map(country => (
                  <option key={country.iso2} value={country.iso2}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
            {error('country') ? (
              <span className="bk-error">{error('country')}</span>
            ) : controller.isCountryDetected ? (
              <span className="bk-help">
                Detectado por la dirección. Puedes cambiarlo.
              </span>
            ) : null}
          </div>

          <Field id="postalCode" label={postalLabel} optional>
            <input
              id="postalCode"
              name="postalCode"
              className="bk-input"
              maxLength={20}
              value={formik.values.postalCode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder={countryIso === 'US' ? '90210' : 'X5000'}
            />
          </Field>
        </div>

        <button
          type="button"
          className="bk-collapse"
          onClick={() => setShowOptional(!showOptional)}
        >
          {showOptional ? '−' : '+'} Agregar WhatsApp, web e Instagram
        </button>

        {showOptional && (
          <>
            <div className="bk-field">
              <label className="bk-label" htmlFor="whatsapp">
                WhatsApp
                <span className="bk-label__optional"> · opcional</span>
              </label>
              <div className="bk-with-icon">
                <span className="bk-phone__prefix">
                  {countryFlag && <span aria-hidden="true">{countryFlag}</span>}
                  {countryPrefix || '+'}
                </span>
                <input
                  id="whatsapp"
                  name="whatsapp"
                  type="tel"
                  className="bk-input"
                  value={formik.values.whatsapp}
                  onChange={formik.handleChange}
                  placeholder={PHONE_PLACEHOLDER}
                />
              </div>
              <span className="bk-help">Sólo si es distinto al teléfono.</span>
            </div>
            <div className="bk-pair">
              <Field id="web" label="Sitio web" optional>
                <input
                  id="web"
                  name="web"
                  className="bk-input"
                  value={formik.values.web}
                  onChange={formik.handleChange}
                  placeholder="https://tucomercio.com"
                />
              </Field>
              <Field id="instagram" label="Instagram" optional>
                <input
                  id="instagram"
                  name="instagram"
                  className="bk-input"
                  value={formik.values.instagram}
                  onChange={formik.handleChange}
                  placeholder="@tucomercio"
                />
              </Field>
            </div>
          </>
        )}
      </section>

      <section className="bk-group">
        <p className="bk-group__title">Tu cuenta</p>

        <div className="bk-pair">
          <Field
            id="contactFirstName"
            label="Nombre"
            error={error('contactFirstName')}
          >
            <input
              id="contactFirstName"
              name="contactFirstName"
              className={inputClass('contactFirstName')}
              maxLength={60}
              value={formik.values.contactFirstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Tu nombre"
            />
          </Field>
          <Field
            id="contactLastName"
            label="Apellido"
            error={error('contactLastName')}
          >
            <input
              id="contactLastName"
              name="contactLastName"
              className={inputClass('contactLastName')}
              maxLength={60}
              value={formik.values.contactLastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Tu apellido"
            />
          </Field>
        </div>

        {/*
          El país ya se eligió arriba, con la dirección: acá sólo se muestra su bandera y
          su prefijo, fijos al costado del campo. El valor que se escribe es el número
          local; el prefijo lo antepone el controller al enviar.
        */}
        <div className="bk-field">
          <label className="bk-label" htmlFor="phone">
            Teléfono
          </label>
          <div
            className={`bk-with-icon${
              error('phone') ? ' bk-with-icon--error' : ''
            }`}
          >
            <span className="bk-phone__prefix">
              {countryFlag && <span aria-hidden="true">{countryFlag}</span>}
              {countryPrefix || '+'}
            </span>
            <input
              id="phone"
              name="phone"
              type="tel"
              className="bk-input"
              value={formik.values.phone}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder={PHONE_PLACEHOLDER}
            />
          </div>
          {error('phone') ? (
            <span className="bk-error">{error('phone')}</span>
          ) : !formik.values.country ? (
            <span className="bk-help">
              Elige la dirección o el país para completar el prefijo.
            </span>
          ) : null}
        </div>

        <div className="bk-pair">
          <div className="bk-field">
            <label className="bk-label" htmlFor="email">
              Email
            </label>
            <div
              className={`bk-with-icon${
                error('email') ? ' bk-with-icon--error' : ''
              }`}
            >
              <MailIcon />
              <input
                id="email"
                name="email"
                type="email"
                className="bk-input"
                maxLength={300}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="contacto@tucomercio.com"
              />
            </div>
            {error('email') && (
              <span className="bk-error">{error('email')}</span>
            )}
          </div>

          <div className="bk-field">
            <label className="bk-label" htmlFor="password">
              Contraseña
            </label>
            <div
              className={`bk-with-icon${
                error('password') ? ' bk-with-icon--error' : ''
              }`}
            >
              <LockIcon />
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                className="bk-input"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Al menos 8 caracteres"
              />
              <button
                type="button"
                className="bk-reveal"
                aria-label={
                  showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'
                }
                aria-pressed={showPassword}
                onClick={() => setShowPassword(!showPassword)}
              >
                <EyeIcon crossed={showPassword} />
              </button>
            </div>
            {error('password') ? (
              <span className="bk-error">{error('password')}</span>
            ) : (
              <PasswordStrength value={formik.values.password} />
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CommerceStep;
